import { markRaw } from 'vue'
import { toast } from 'vue-sonner'

import ToastCard from '@/shared/components/toast/ToastCard.vue'

import type { ToastCardProps, ToastSeverity } from '@/shared/components/toast/ToastCard.vue'

export type AppToastOptions = Omit<ToastCardProps, 'severity' | 'summary'> & {
  duration?: number
  /** A toast shown with the id of one still showing replaces it, and `appToast.dismiss` closes it */
  id?: string
}

const DEFAULT_DURATION_MS = 3000

// markRaw once at module level — the card is a static component, not reactive state
const card = markRaw(ToastCard)

const show = (severity: ToastSeverity, summary: string, options: AppToastOptions = {}) => {
  const { duration, id, action, ...content } = options
  // vue-sonner names the toast when no id is given, so the action can close the one it sits in
  const toastId = toast(card, {
    id,
    componentProps: {
      severity,
      summary,
      ...content,
      action: action && {
        label: action.label,
        onClick: () => {
          toast.dismiss(toastId)
          action.onClick()
        }
      }
    } satisfies ToastCardProps,
    // Errors stay until dismissed, matching the previous PrimeVue behavior
    duration: duration ?? (severity === 'error' ? Infinity : DEFAULT_DURATION_MS)
  })
}

export const appToast = {
  success: (summary: string, options?: AppToastOptions) => show('success', summary, options),
  info: (summary: string, options?: AppToastOptions) => show('info', summary, options),
  warning: (summary: string, options?: AppToastOptions) => show('warning', summary, options),
  error: (summary: string, options?: AppToastOptions) => show('error', summary, options),
  dismiss: (id: string) => {
    toast.dismiss(id)
  }
}
